import type { ReactNode } from 'react';
import type { PageTourKey } from '@/lib/tours/registry';
import { PageTourHelp } from './PageTourHelp';

export interface PageFeatureTourOption {
  tourKey: PageTourKey;
  label: string;
  description?: string;
  onBeforeStart?: () => void;
  /** When set, runs instead of the default registry tour launch (after onBeforeStart). */
  onStart?: () => void;
}

interface PageFeatureHelpProps {
  title: string;
  children: ReactNode;
  tours?: PageFeatureTourOption[];
}

/**
 * Top-bar page help: feature overview copy plus optional guided tours for the main page surface
 * (not drawer forms — use DrawerTourHelp for those).
 */
export function PageFeatureHelp({ title, children, tours = [] }: PageFeatureHelpProps) {
  return (
    <div className="space-y-4 text-sm text-muted-foreground">
      <div>
        <h3 className="mb-2 text-sm font-semibold text-foreground">{title}</h3>
        {children}
      </div>
      {tours.length > 0 ? (
        <div className="space-y-3 border-t border-border pt-3">
          <p className="text-xs font-medium text-foreground">Guided tours</p>
          {tours.map((tour) => (
            <PageTourHelp
              key={tour.tourKey}
              tourKey={tour.tourKey}
              buttonLabel={tour.label}
              onBeforeStart={tour.onBeforeStart}
              onStart={tour.onStart}
            >
              {tour.description ? <p className="text-xs">{tour.description}</p> : null}
            </PageTourHelp>
          ))}
        </div>
      ) : null}
    </div>
  );
}
